import React from 'react';
import { Link } from 'react-router-dom';
import type { SupportTicketViewModel } from 'api/types/support-ticket';
import ChatPanel from './ChatPanel';

export interface SupportTicketDetailProps {
  ticket: SupportTicketViewModel;
  currentUserId: string;
  backPath?: string;
  backLabel?: string;
  /** When true, chat is read-only (e.g. ticket resolved). */
  isResolved?: boolean;
}

const SupportTicketDetail: React.FC<SupportTicketDetailProps> = ({
  ticket,
  currentUserId,
  backPath = '/teknik-destek',
  backLabel = 'Talep listesine dön',
  isResolved = false,
}) => {
  const hasChat = Boolean(ticket.conversationId);

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <Link className="btn btn-link text-muted text-decoration-none me-2" to={backPath}>
            <i className="fas fa-arrow-left" />
          </Link>
          <h5 className="mb-0">
            Talep #{typeof ticket.id === 'string' ? ticket.id.slice(0, 8) : ticket.id}
          </h5>
        </div>
        <span className={`badge ${isResolved ? 'bg-success' : 'bg-secondary'}`}>
          {ticket.supportStatusName ?? (isResolved ? 'Çözüldü' : 'Beklemede')}
        </span>
      </div>
      {isResolved && (
        <div className="alert alert-success rounded-0 mb-0 py-2" role="status">
          <i className="fas fa-check-circle me-2" />
          Bu talep çözüldü olarak işaretlendi. Sohbete artık mesaj eklenemez.
        </div>
      )}
      {ticket.userProfile && (
        <div className="card-body border-bottom sb-surface-muted">
          <h6 className="text-muted text-uppercase small mb-3">Talep sahibi</h6>
          <div className="d-flex align-items-start gap-3">
            {ticket.userProfile.avatarUrl ? (
              <img
                src={ticket.userProfile.avatarUrl}
                alt=""
                className="rounded-circle"
                width={56}
                height={56}
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <div
                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white flex-shrink-0"
                style={{ width: 56, height: 56 }}
              >
                <i className="fas fa-user" aria-hidden />
              </div>
            )}
            <div className="min-w-0">
              <div className="fw-semibold">{ticket.userProfile.displayName}</div>
              {ticket.userProfile.subtitle ? (
                <div className="text-muted small text-break">{ticket.userProfile.subtitle}</div>
              ) : null}
              {ticket.userProfile.email ? (
                <div className="small mt-1">
                  <span className="text-muted">E-posta: </span>
                  {ticket.userProfile.email}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <div className="card-body">
        <dl className="row mb-0">
          <dt className="col-sm-3">Kategori</dt>
          <dd className="col-sm-9">{ticket.supportCategoryName ?? '-'}</dd>
          <dt className="col-sm-3">Oluşturulma</dt>
          <dd className="col-sm-9">
            {ticket.createdAtUtc ? new Date(ticket.createdAtUtc).toLocaleString('tr-TR') : '-'}
          </dd>
          <dt className="col-sm-3">Açıklama</dt>
          <dd className="col-sm-9">{ticket.details ?? '-'}</dd>
        </dl>
      </div>
      {hasChat && (
        <div className="card-body border-top pt-3">
          <ChatPanel
            conversationId={ticket.conversationId!}
            currentUserId={currentUserId}
            isReadOnly={isResolved}
            title="Destek sohbeti"
          />
        </div>
      )}
      {!hasChat && (
        <div className="card-body border-top">
          <p className="text-muted mb-0">
            <i className="fas fa-info-circle me-2" />
            Destek ekibi talebinizi inceleyecek. Sohbet başladığında burada görünecektir.
          </p>
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetail;
